import { Notification, NotificationToUpdate } from 'clipsal-cortex-types/src/api/api-notifications';
import { PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';

export type NotificationsList = PaginatedResponseV2<Notification>;

/** The subset of a list query's params the optimistic patch needs to respect. */
type NotificationListFilters = { archived: boolean; read?: boolean };

export type NotificationOptimisticChange = { type: 'delete' } | { type: 'update'; body: NotificationToUpdate };

/**
 * Optimistically applies a pending mutation to one already-cached notification list, so the UI reflects the
 * change on tap instead of waiting for the server round-trip plus the invalidation refetch. Mutates `draft`
 * in place (intended for RTK Query's `updateQueryData`) and returns silently when the affected notification
 * isn't in this particular list.
 *
 * `body.read` is the boolean from the PATCH body; the cached `read` field is the GET response's timestamp
 * (`string | null`), so we map `true`/`false` to a timestamp/null to keep the unread indicator consistent.
 */
export function applyOptimisticNotificationChange(
  draft: NotificationsList,
  listArgs: NotificationListFilters,
  notificationId: string,
  change: NotificationOptimisticChange
) {
  const index = draft.results.findIndex((n) => n.notification_id === notificationId);
  if (index === -1) return;

  const removeFromList = () => {
    draft.results.splice(index, 1);
    draft.count = Math.max(0, draft.count - 1);
  };

  if (change.type === 'delete') {
    removeFromList();
    return;
  }

  const { body } = change;
  // Archiving removes the notification from every non-archived list (the archive screen has its own query)
  if (body.archived && !listArgs.archived) {
    removeFromList();
    return;
  }
  if (body.read !== undefined) {
    // A list filtered by the opposite read state no longer contains this notification
    if (listArgs.read !== undefined && listArgs.read !== body.read) {
      removeFromList();
      return;
    }
    draft.results[index].read = body.read ? new Date().toISOString() : null;
  }
}
